import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, Plus, Pencil, Trash2, Tag, Check } from 'lucide-react'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Badge } from '@/common/ui/badge'
import { ScrollArea } from '@/common/ui/scroll-area'
import { Skeleton } from '@/common/ui/skeleton'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'

import {
	useQueryTags,
	useMutationCreateTag,
	useMutationUpdateTag,
	useMutationDeleteTag,
	useMutationAddTagsToRoom,
	useMutationRemoveTagFromRoom,
} from '../../api/hooks'

import type { components } from '@/schema'

type CustomerTagResponse = components['schemas']['CustomerTagResponse']

// Predefined colors for tags
const TAG_COLORS = [
	'#EF4444', // Red
	'#F97316', // Orange
	'#F59E0B', // Amber
	'#EAB308', // Yellow
	'#84CC16', // Lime
	'#22C55E', // Green
	'#10B981', // Emerald
	'#14B8A6', // Teal
	'#06B6D4', // Cyan
	'#0EA5E9', // Sky
	'#3B82F6', // Blue
	'#6366F1', // Indigo
	'#8B5CF6', // Violet
	'#A855F7', // Purple
	'#D946EF', // Fuchsia
	'#EC4899', // Pink
	'#F43F5E', // Rose
	'#6B7280', // Gray
]

interface TagManagerPanelProps {
	roomId: string
	currentTags: CustomerTagResponse[]
	playerTags?: CustomerTagResponse[]
	onClose: () => void
}

export function TagManagerPanel({
	roomId,
	currentTags,
	playerTags,
	onClose,
}: TagManagerPanelProps) {
	const { data: allTags, isLoading } = useQueryTags()
	const addTagsMutation = useMutationAddTagsToRoom()
	const removeTagMutation = useMutationRemoveTagFromRoom()

	const currentTagIds = currentTags.map((t) => t.id)

	// Filter player tags that are not already in the room
	const availablePlayerTags = playerTags?.filter(
		(pt) => !currentTagIds.includes(pt.id),
	)

	const handleToggleTag = (tagId: string) => {
		if (currentTagIds.includes(tagId)) {
			removeTagMutation.mutate({ roomId, tagId })
		} else {
			addTagsMutation.mutate({ roomId, tagIds: [tagId] })
		}
	}

	const handleAddPlayerTag = (tagId: string) => {
		addTagsMutation.mutate({ roomId, tagIds: [tagId] })
	}

	const handleAddAllPlayerTags = () => {
		if (availablePlayerTags && availablePlayerTags.length > 0) {
			addTagsMutation.mutate({
				roomId,
				tagIds: availablePlayerTags.map((t) => t.id),
			})
		}
	}

	return (
		<div className="border-t bg-muted/30">
			<div className="flex items-center justify-between border-b px-4 py-2">
				<div className="flex items-center gap-2">
					<Tag className="h-4 w-4 text-primary" />
					<span className="text-sm font-medium">จัดการแท็ก</span>
				</div>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6"
					onClick={onClose}
				>
					<X className="h-4 w-4" />
				</Button>
			</div>

			<ScrollArea className="max-h-64">
				{/* Player Tags Section - Show if player has tags that aren't in the room */}
				{availablePlayerTags && availablePlayerTags.length > 0 && (
					<div className="border-b px-4 py-3">
						<div className="mb-2 flex items-center justify-between">
							<span className="text-xs font-medium text-muted-foreground">
								แท็กจากผู้เล่น
							</span>
							<Button
								variant="ghost"
								size="sm"
								className="h-6 px-2 text-xs"
								onClick={handleAddAllPlayerTags}
								disabled={addTagsMutation.isPending}
							>
								<Plus className="mr-1 h-3 w-3" />
								เพิ่มทั้งหมด
							</Button>
						</div>
						<div className="flex flex-wrap gap-2">
							{availablePlayerTags.map((tag) => (
								<button
									key={tag.id}
									onClick={() => handleAddPlayerTag(tag.id)}
									disabled={addTagsMutation.isPending}
									className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors hover:bg-muted"
									style={{
										borderColor: tag.color,
										color: tag.color,
									}}
								>
									<Plus className="h-3 w-3" />
									{tag.name}
								</button>
							))}
						</div>
					</div>
				)}

				{/* All Tags Section */}
				{isLoading ? (
					<div className="flex flex-wrap gap-2 p-4">
						{Array.from({ length: 5 }).map((_, i) => (
							<Skeleton key={i} className="h-6 w-16 rounded-full" />
						))}
					</div>
				) : !allTags || allTags.length === 0 ? (
					<div className="p-4 text-center text-sm text-muted-foreground">
						ยังไม่มีแท็ก กรุณาสร้างแท็กในหน้าตั้งค่า
					</div>
				) : (
					<div className="p-4">
						<span className="mb-2 block text-xs font-medium text-muted-foreground">
							แท็กทั้งหมด
						</span>
						<div className="flex flex-wrap gap-2">
							{allTags.map((tag) => {
								const isSelected = currentTagIds.includes(tag.id)
								return (
									<button
										key={tag.id}
										onClick={() => handleToggleTag(tag.id)}
										disabled={
											addTagsMutation.isPending || removeTagMutation.isPending
										}
										className={cn(
											'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors',
											isSelected
												? 'border-transparent text-white'
												: 'border-current hover:bg-muted',
										)}
										style={{
											backgroundColor: isSelected ? tag.color : 'transparent',
											color: isSelected ? 'white' : tag.color,
											borderColor: tag.color,
										}}
									>
										{isSelected && <Check className="h-3 w-3" />}
										{tag.name}
									</button>
								)
							})}
						</div>
					</div>
				)}
			</ScrollArea>
		</div>
	)
}

// Full Tag Manager for settings page
const tagSchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อแท็ก'),
	color: z.string().min(1, 'กรุณาเลือกสี'),
})

type TagFormData = z.infer<typeof tagSchema>

export function TagsManager() {
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [editingTag, setEditingTag] = useState<CustomerTagResponse | null>(null)

	const { data: tags, isLoading } = useQueryTags()
	const createMutation = useMutationCreateTag()
	const updateMutation = useMutationUpdateTag()
	const deleteMutation = useMutationDeleteTag()

	const form = useForm<TagFormData>({
		resolver: zodResolver(tagSchema),
		defaultValues: {
			name: '',
			color: TAG_COLORS[0],
		},
	})

	const selectedColor = form.watch('color')

	const handleOpenCreate = () => {
		setEditingTag(null)
		form.reset({
			name: '',
			color: TAG_COLORS[0],
		})
		setIsDialogOpen(true)
	}

	const handleOpenEdit = (tag: CustomerTagResponse) => {
		setEditingTag(tag)
		form.reset({
			name: tag.name,
			color: tag.color,
		})
		setIsDialogOpen(true)
	}

	const handleSubmit = (data: TagFormData) => {
		if (editingTag) {
			updateMutation.mutate(
				{ id: editingTag.id, data },
				{
					onSuccess: () => {
						setIsDialogOpen(false)
						setEditingTag(null)
					},
				},
			)
		} else {
			createMutation.mutate(data, {
				onSuccess: () => {
					setIsDialogOpen(false)
				},
			})
		}
	}

	const handleDelete = (id: string) => {
		if (confirm('ต้องการลบแท็กนี้หรือไม่? แท็กจะถูกลบออกจากทุกห้องแชท')) {
			deleteMutation.mutate(id)
		}
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-medium">แท็กลูกค้า</h3>
					<p className="text-sm text-muted-foreground">
						สร้างแท็กเพื่อจัดกลุ่มและค้นหาแชทได้ง่ายขึ้น
					</p>
				</div>
				<Button onClick={handleOpenCreate}>
					<Plus className="mr-2 h-4 w-4" />
					เพิ่มแท็ก
				</Button>
			</div>

			{isLoading ? (
				<div className="flex flex-wrap gap-2">
					{Array.from({ length: 5 }).map((_, i) => (
						<Skeleton key={i} className="h-8 w-20 rounded-full" />
					))}
				</div>
			) : !tags || tags.length === 0 ? (
				<div className="rounded-lg border border-dashed p-8 text-center">
					<Tag className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
					<p className="text-sm text-muted-foreground">
						ยังไม่มีแท็ก คลิกปุ่มด้านบนเพื่อเพิ่ม
					</p>
				</div>
			) : (
				<div className="flex flex-wrap gap-2">
					{tags.map((tag) => (
						<div
							key={tag.id}
							className="group relative inline-flex items-center"
						>
							<Badge
								variant="outline"
								className="pr-8 text-sm"
								style={{ borderColor: tag.color, color: tag.color }}
							>
								<span
									className="mr-2 h-2 w-2 rounded-full"
									style={{ backgroundColor: tag.color }}
								/>
								{tag.name}
							</Badge>
							<div className="absolute right-1 flex opacity-0 transition-opacity group-hover:opacity-100">
								<Button
									variant="ghost"
									size="icon"
									className="h-5 w-5"
									onClick={() => handleOpenEdit(tag)}
								>
									<Pencil className="h-3 w-3" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="h-5 w-5"
									onClick={() => handleDelete(tag.id)}
								>
									<Trash2 className="h-3 w-3" />
								</Button>
							</div>
						</div>
					))}
				</div>
			)}

			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editingTag ? 'แก้ไขแท็ก' : 'เพิ่มแท็ก'}</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="space-y-4"
						>
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ชื่อแท็ก</FormLabel>
										<FormControl>
											<Input placeholder="เช่น VIP, ลูกค้าใหม่" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="color"
								render={({ field }) => (
									<FormItem>
										<FormLabel>สี</FormLabel>
										<FormControl>
											<Popover>
												<PopoverTrigger asChild>
													<Button
														variant="outline"
														className="w-full justify-start"
													>
														<span
															className="mr-2 h-4 w-4 rounded"
															style={{ backgroundColor: field.value }}
														/>
														{field.value}
													</Button>
												</PopoverTrigger>
												<PopoverContent className="w-64">
													<div className="grid grid-cols-6 gap-2">
														{TAG_COLORS.map((color) => (
															<button
																key={color}
																type="button"
																onClick={() => field.onChange(color)}
																className={cn(
																	'h-8 w-8 rounded-md transition-transform hover:scale-110',
																	selectedColor === color &&
																		'ring-2 ring-primary ring-offset-2',
																)}
																style={{ backgroundColor: color }}
															/>
														))}
													</div>
												</PopoverContent>
											</Popover>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Preview */}
							<div>
								<FormLabel>ตัวอย่าง</FormLabel>
								<div className="mt-2">
									<Badge
										variant="outline"
										className="text-sm"
										style={{
											borderColor: selectedColor,
											color: selectedColor,
										}}
									>
										<span
											className="mr-2 h-2 w-2 rounded-full"
											style={{ backgroundColor: selectedColor }}
										/>
										{form.watch('name') || 'ชื่อแท็ก'}
									</Badge>
								</div>
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={() => setIsDialogOpen(false)}
								>
									ยกเลิก
								</Button>
								<Button
									type="submit"
									disabled={
										createMutation.isPending || updateMutation.isPending
									}
								>
									{editingTag ? 'บันทึก' : 'เพิ่ม'}
								</Button>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</div>
	)
}
